// admin/src/pages/ManageEnjoyTravel.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import adminApi from '../services/adminApi';
import './ManageEnjoyTravel.css';

const SERVER_URL = process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:5000';

const resolveImageUrl = (imagePath) => {
  if (!imagePath) return '';
  if (imagePath.startsWith('http') || imagePath.startsWith('data:')) return imagePath;
  if (imagePath.startsWith('/uploads/')) return `${SERVER_URL}${imagePath}`;
  return imagePath;
};

/* ═══════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════ */
const ManageEnjoyTravel = () => {
  const [slides, setSlides] = useState([]);
  const [description, setDescription] = useState('');
  const [descDraft, setDescDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [descSaving, setDescSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSlide, setSelectedSlide] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const showSuccess = (msg) => { setSuccessMsg(msg); setErrorMsg(''); setTimeout(() => setSuccessMsg(''), 3000); };
  const showError = (msg) => { setErrorMsg(msg); setSuccessMsg(''); setTimeout(() => setErrorMsg(''), 5000); };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminApi.getEnjoyTravelData();
      if (data.success !== false) {
        const normalized = (data.slides || []).map((s) => ({
          ...s,
          id: s._id,
          image: resolveImageUrl(s.image),
        }));
        setSlides(normalized.sort((a, b) => a.order - b.order));
        setDescription(data.description || '');
        setDescDraft(data.description || '');
      }
    } catch (err) {
      showError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ─── Description save ─── */
  const handleSaveDescription = async () => {
    if (!descDraft.trim()) return showError('Description cannot be empty');
    try {
      setDescSaving(true);
      await adminApi.updateEnjoyTravelConfig({ description: descDraft.trim() });
      setDescription(descDraft.trim());
      showSuccess('Description updated');
    } catch {
      showError('Failed to update description');
    } finally {
      setDescSaving(false);
    }
  };

  /* ─── Slide actions ─── */
  const moveSlide = async (index, direction) => {
    const sorted = [...slides];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= sorted.length) return;
    [sorted[index], sorted[targetIndex]] = [sorted[targetIndex], sorted[index]];
    try {
      await adminApi.reorderEnjoyTravelSlides(sorted.map((s) => s._id));
      await fetchData();
      showSuccess('Reordered');
    } catch { showError('Failed to reorder'); }
  };

  const handleAdd = async (formData) => {
    try {
      await adminApi.createEnjoyTravelSlide(formData);
      await fetchData();
      setShowAddModal(false);
      showSuccess('Slide added');
    } catch (err) { showError(err.message || 'Failed to add slide'); }
  };

  const handleUpdate = async (formData) => {
    try {
      await adminApi.updateEnjoyTravelSlide(selectedSlide._id, formData);
      await fetchData();
      setShowEditModal(false);
      setSelectedSlide(null);
      showSuccess('Slide updated');
    } catch (err) { showError(err.message || 'Failed to update slide'); }
  };

  const handleDelete = async () => {
    if (!selectedSlide) return;
    try {
      await adminApi.deleteEnjoyTravelSlide(selectedSlide._id);
      await fetchData();
      setShowDeleteModal(false);
      setSelectedSlide(null);
      showSuccess('Slide deleted');
    } catch { showError('Failed to delete'); }
  };

  const handleToggle = async (id) => {
    try {
      await adminApi.toggleEnjoyTravelSlide(id);
      await fetchData();
      showSuccess('Status updated');
    } catch { showError('Failed to toggle'); }
  };

  if (loading) {
    return (
      <div className="etm">
        <div className="etm__header"><h1 className="etm__title">Enjoy Travel Manager</h1></div>
        <div className="etm__empty"><div className="hm__loader" /><p>Loading...</p></div>
      </div>
    );
  }

  const activeCount = slides.filter((s) => s.isActive).length;

  return (
    <div className="etm">
      {/* Header */}
      <div className="etm__header">
        <div>
          <h1 className="etm__title">Enjoy Travel Manager</h1>
          <p className="etm__subtitle">{activeCount} active of {slides.length} slides</p>
        </div>
        <button className="hm__btn hm__btn--primary" onClick={() => setShowAddModal(true)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Add Slide
        </button>
      </div>

      {/* Messages */}
      {successMsg && (
        <div className="hm__msg hm__msg--success">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          {successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="hm__msg hm__msg--error">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
          {errorMsg}
        </div>
      )}

      {/* Description editor */}
      <div className="etm__desc-card">
        <h2 className="etm__section-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
          Section Description
        </h2>
        <p className="etm__desc-hint">This text appears below "Enjoy your travel" on the homepage.</p>
        <textarea
          className="etm__desc-textarea"
          value={descDraft}
          onChange={(e) => setDescDraft(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="Enter section description..."
        />
        <div className="etm__desc-footer">
          <span className="etm__char-count">{descDraft.length}/500</span>
          <button
            className="hm__btn hm__btn--primary"
            onClick={handleSaveDescription}
            disabled={descSaving || descDraft === description}
          >
            {descSaving ? 'Saving...' : 'Save Description'}
          </button>
        </div>
      </div>

      {/* Slides grid */}
      <div className="etm__section-title-row">
        <h2 className="etm__section-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21 15 16 10 5 21"/>
          </svg>
          Slides
        </h2>
      </div>

      {slides.length === 0 ? (
        <div className="hm__empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21 15 16 10 5 21"/>
          </svg>
          <h3>No slides yet</h3>
          <p>Add your first slide to the Enjoy Travel section</p>
          <button className="hm__btn hm__btn--primary" onClick={() => setShowAddModal(true)}>Add First Slide</button>
        </div>
      ) : (
        <div className="hm__grid">
          {slides.map((slide, index) => (
            <div key={slide._id} className={`hm__card${!slide.isActive ? ' hm__card--inactive' : ''}`}>
              <div className="hm__card-img">
                <img src={slide.image} alt={slide.label} />
                {!slide.isActive && <div className="hm__card-badge hm__card-badge--inactive">Inactive</div>}
                <div className="hm__card-badge hm__card-badge--order">#{index + 1}</div>
                <div className="hm__card-overlay">
                  <button className="hm__card-action" onClick={() => { setSelectedSlide(slide); setShowEditModal(true); }} title="Edit">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </button>
                  <button className="hm__card-action hm__card-action--danger" onClick={() => { setSelectedSlide(slide); setShowDeleteModal(true); }} title="Delete">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                  </button>
                </div>
              </div>
              <div className="hm__card-body">
                <h3 className="hm__card-title">{slide.label}</h3>
                <div className="hm__card-footer">
                  <label className="hm__toggle">
                    <input type="checkbox" checked={slide.isActive} onChange={() => handleToggle(slide._id)} />
                    <span className="hm__toggle-track" />
                    <span className="hm__toggle-label">{slide.isActive ? 'Active' : 'Inactive'}</span>
                  </label>
                  <div className="hm__reorder">
                    <button className="hm__reorder-btn" onClick={() => moveSlide(index, 'up')} disabled={index === 0}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="18 15 12 9 6 15"/></svg>
                    </button>
                    <button className="hm__reorder-btn" onClick={() => moveSlide(index, 'down')} disabled={index === slides.length - 1}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <SlideFormModal title="Add New Slide" onClose={() => setShowAddModal(false)} onSave={handleAdd} />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedSlide && (
        <SlideFormModal
          title="Edit Slide"
          initialData={selectedSlide}
          onClose={() => { setShowEditModal(false); setSelectedSlide(null); }}
          onSave={handleUpdate}
        />
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedSlide && (
        <div className="hm__modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="hm__modal hm__modal--sm" onClick={(e) => e.stopPropagation()}>
            <div className="hm__delete-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </div>
            <h3 className="hm__delete-title">Delete Slide?</h3>
            <p className="hm__delete-text">Are you sure you want to delete "<strong>{selectedSlide.label}</strong>"? This cannot be undone.</p>
            <div className="hm__delete-actions">
              <button className="hm__btn hm__btn--ghost" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button className="hm__btn hm__btn--danger" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════
   SLIDE FORM MODAL
   ═══════════════════════════════════════ */
const SlideFormModal = ({ title, initialData, onClose, onSave }) => {
  const [label, setLabel] = useState(initialData?.label || '');
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [imagePreview, setImagePreview] = useState(initialData?.image || '');
  const [isActive, setIsActive] = useState(initialData?.isActive !== undefined ? initialData.isActive : true);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (initialData?.image && initialData.image.startsWith('http') && !initialData.image.includes('/uploads/')) {
      setImageUrl(initialData.image);
    }
  }, [initialData]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const valid = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!valid.includes(file.type)) return setErrors((p) => ({ ...p, image: 'JPG, PNG or WebP only' }));
    if (file.size > 5 * 1024 * 1024) return setErrors((p) => ({ ...p, image: 'Max 5MB' }));
    setErrors((p) => ({ ...p, image: '' }));
    const reader = new FileReader();
    reader.onload = (ev) => { setImageFile(file); setImageUrl(''); setImagePreview(ev.target.result); };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null); setImageUrl(''); setImagePreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUrlInput = (url) => {
    setImageUrl(url); setImageFile(null); setImagePreview(url);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    const errs = {};
    if (!label.trim()) errs.label = 'Label is required';
    if (!initialData && !imageFile && !imageUrl) errs.image = 'Image is required';
    if (Object.keys(errs).length > 0) return setErrors(errs);

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('label', label.trim());
      formData.append('isActive', isActive);
      if (imageFile) formData.append('image', imageFile);
      else if (imageUrl) formData.append('imageUrl', imageUrl);
      await onSave(formData);
    } catch (err) {
      setErrors({ general: err.message || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="hm__modal-overlay" onClick={onClose}>
      <div className="hm__modal hm__modal--form" onClick={(e) => e.stopPropagation()}>
        <div className="hm__modal-header">
          <h2>{title}</h2>
          <button className="hm__modal-close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div className="hm__modal-body">
          {errors.general && <div className="hm__msg hm__msg--error" style={{ margin: 0 }}>{errors.general}</div>}

          <div className="hm__form-field">
            <label className="hm__form-label">Location Label <span className="hm__required">*</span></label>
            <input
              type="text"
              value={label}
              onChange={(e) => { setLabel(e.target.value); setErrors((p) => ({ ...p, label: '' })); }}
              placeholder="e.g. Lighthouse Fort of Arnel, Azores"
              className={`hm__form-input${errors.label ? ' hm__form-input--error' : ''}`}
            />
            {errors.label && <span className="hm__form-error">{errors.label}</span>}
          </div>

          <div className="hm__form-field">
            <label className="hm__form-label">
              Image <span className="hm__required">{!initialData ? '*' : ''}</span>
              <span className="hm__form-hint"> 800×600px recommended</span>
            </label>
            {imagePreview ? (
              <div className="hm__img-preview">
                <img src={imagePreview} alt="Preview" />
                <button className="hm__img-remove" onClick={removeImage}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                  Remove
                </button>
              </div>
            ) : (
              <div className="hm__upload-area">
                <input
                  type="file" ref={fileInputRef}
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileChange}
                  className="hm__file-input" id="enjoy-img"
                />
                <label htmlFor="enjoy-img" className="hm__upload-label">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  <span>Click to upload</span>
                  <span className="hm__upload-hint">JPG, PNG, WebP (max 5MB)</span>
                </label>
              </div>
            )}
            <div className="hm__url-row">
              <span className="hm__url-or">or paste URL:</span>
              <input
                type="text" value={imageUrl}
                onChange={(e) => { handleUrlInput(e.target.value); setErrors((p) => ({ ...p, image: '' })); }}
                placeholder="https://example.com/photo.jpg"
                className="hm__form-input hm__form-input--sm"
              />
            </div>
            {errors.image && <span className="hm__form-error">{errors.image}</span>}
          </div>

          <div className="hm__form-field">
            <label className="hm__toggle">
              <input type="checkbox" checked={isActive} onChange={() => setIsActive(!isActive)} />
              <span className="hm__toggle-track" />
              <span className="hm__toggle-label">{isActive ? 'Active (visible)' : 'Inactive (hidden)'}</span>
            </label>
          </div>
        </div>
        <div className="hm__modal-footer">
          <button className="hm__btn hm__btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="hm__btn hm__btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : initialData ? 'Save Changes' : 'Add Slide'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageEnjoyTravel;
